import { Mark, mergeAttributes } from "@tiptap/core";

export interface CommentOptions {
  HTMLAttributes: Record<string, any>;
  onCommentActivated?: (commentId: string | null) => void;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    comment: {
      /**
       * Set a comment mark
       */
      setComment: (commentId: string) => ReturnType;
      /**
       * Unset a comment mark
       */
      unsetComment: (commentId: string) => ReturnType;
    };
  }
}

export const CommentExtension = Mark.create<CommentOptions>({
  name: "comment",

  // Allow multiple comment marks on the same text range.
  // Without this, ProseMirror defaults excludes to the mark's own name,
  // meaning a comment mark excludes other comment marks. This breaks
  // overlapping comments (e.g. a reply that anchors to the same text
  // as the original comment — common in Word DOCX imports).
  excludes: "",

  addOptions() {
    return {
      HTMLAttributes: {
        class: "tiptap-comment",
      },
      onCommentActivated: undefined,
    };
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-comment-id"),
        renderHTML: (attributes) => {
          if (!attributes.commentId) {
            return {};
          }
          return {
            "data-comment-id": attributes.commentId,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-comment-id]",
        getAttrs: (element) => {
          const commentId = (element as HTMLElement).getAttribute("data-comment-id");
          return commentId ? { commentId } : false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addCommands() {
    return {
      setComment:
        (commentId: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { commentId });
        },
      unsetComment:
        (commentId: string) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          const markType = state.schema.marks[this.name];

          if (!markType) return false;

          // Search the whole document for this comment ID and remove it.
          // Remove the SPECIFIC matched mark instance (type + attrs), NOT the
          // mark type — passing markType would strip EVERY comment mark in the
          // range, permanently destroying overlapping comment threads (common
          // on Word imports and replies anchored to the same text).
          if (dispatch) {
            doc.descendants((node, pos) => {
              if (node.marks) {
                node.marks.forEach((mark) => {
                  if (mark.type.name === this.name && mark.attrs.commentId === commentId) {
                    tr.removeMark(pos, pos + node.nodeSize, mark);
                  }
                });
              }
            });
            dispatch(tr);
          }

          return true;
        },
    };
  },

  onSelectionUpdate() {
    const { onCommentActivated } = this.options;
    if (!onCommentActivated) return;

    const { state } = this.editor;
    const { from, to } = state.selection;

    let activeCommentId: string | null = null;

    // Check if cursor is within a comment
    state.doc.nodesBetween(from, to, (node) => {
      if (node.marks) {
        node.marks.forEach((mark) => {
          if (mark.type.name === this.name && mark.attrs.commentId) {
            activeCommentId = mark.attrs.commentId;
          }
        });
      }
    });

    onCommentActivated(activeCommentId);
  },
});

export default CommentExtension;
